import { createContext, useContext, useMemo, useState } from 'react';

/**
 * Bidirectional hover-sync between the line-items table and the source document
 * viewer (Extend OCR blocks). A line row and its originating source region share
 * a key; hovering either highlights both. Phase 0 establishes the seam (the
 * document side is wired in the provenance phase) so the table and viewer stay
 * decoupled — neither imports the other, both read this context.
 */
type HoverSyncValue = {
  /** Stable line-item id currently hovered (from the table), or null. */
  hoveredLineId: string | null;
  /** Source block id currently hovered (from the document viewer), or null. */
  hoveredBlockId: string | null;
  /** Source block id pinned by a click (persists until re-clicked), or null. */
  selectedBlockId: string | null;
  setHoveredLineId: (id: string | null) => void;
  setHoveredBlockId: (id: string | null) => void;
  setSelectedBlockId: (id: string | null) => void;
};

const HoverSyncContext = createContext<HoverSyncValue | null>(null);

export function HoverSyncProvider({ children }: { children: React.ReactNode }) {
  const [hoveredLineId, setHoveredLineId] = useState<string | null>(null);
  const [hoveredBlockId, setHoveredBlockId] = useState<string | null>(null);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);

  const value = useMemo<HoverSyncValue>(
    () => ({
      hoveredLineId,
      hoveredBlockId,
      selectedBlockId,
      setHoveredLineId,
      setHoveredBlockId,
      setSelectedBlockId,
    }),
    [hoveredLineId, hoveredBlockId, selectedBlockId],
  );

  return (
    <HoverSyncContext.Provider value={value}>
      {children}
    </HoverSyncContext.Provider>
  );
}

export function useHoverSync(): HoverSyncValue {
  const ctx = useContext(HoverSyncContext);
  if (!ctx) {
    throw new Error('useHoverSync must be used within a HoverSyncProvider');
  }
  return ctx;
}
